import * as Sentry from '@sentry/react';
import config from 'src/config';

interface ErrorServiceInterface {
    report: (error: Error) => void;
    shouldIgnoreError: (error: Error) => boolean;
}

export class ErrorService implements ErrorServiceInterface {
    private isReportingServiceInitialized = false;

    // List of error patterns that should not be sent to Sentry
    private readonly ignoredErrorPatterns = [
        // Authentication related errors (expected user flow)
        /401/i,
        /unauthorized/i,
        /authentication/i,
        /token.*expired/i,
        /session.*expired/i,
        /invalid.*token/i,
        /access.*denied/i,
        /forbidden/i,
        /403/i,

        // Network related errors that are often temporary
        /network.*error/i,
        /connection.*refused/i,
        /timeout/i,
        /aborted/i,
        /cancelled/i,

        // Expected CMS/API errors
        /404/i,
        /not.*found/i,
        /video.*article.*not.*found/i,

        // Browser/user related errors
        /user.*denied/i,
        /permission.*denied/i,
        /quota.*exceeded/i,
        /storage.*full/i,

        // Development related errors
        /chunk.*load.*error/i,
        /loading.*chunk.*\d+.*failed/i,
    ];

    public constructor() {
        this.initializeReportingService();
    }

    public shouldIgnoreError(error: Error): boolean {
        const errorMessage = error?.message || '';
        const errorStack = error?.stack || '';
        const errorString = `${errorMessage} ${errorStack}`.toLowerCase();

        // Check if error matches any ignored patterns
        return this.ignoredErrorPatterns.some(pattern => pattern.test(errorString));
    }

    public report(error: Error) {
        // Don't report errors that should be ignored
        if (this.shouldIgnoreError(error)) {
            console.info('Ignoring expected error from Sentry reporting:', {
                message: error.message,
                type: error.name,
            });
            return;
        }

        if (this.isReportingServiceInitialized) {
            Sentry.captureException(error);
        } else {
            console.error('Error occurred but Sentry not initialized:', error);
        }
    }

    public initializeReportingService() {
        if (this.isReportingServiceInitialized) {
            return;
        }
        if (config.shouldReportErrors) {
            Sentry.init({
                environment: config.deploymentEnv,
                dsn: config.sentryDsn,
                ignoreErrors: [
                    'AbortError',
                    // Additional client-side errors to ignore
                    'Non-Error promise rejection captured',
                    'ResizeObserver loop limit exceeded',
                    'Script error.',
                    'Network request failed',
                    // Auth related errors
                    /401/,
                    /unauthorized/i,
                    /authentication.*failed/i,
                    /token.*expired/i,
                    /session.*expired/i,
                    // CMS/API errors
                    /404/,
                    /not.*found/i,
                ],
                beforeSend: (event, hint) => {
                    // Additional filtering at Sentry level
                    const error = hint.originalException as Error;
                    if (error && this.shouldIgnoreError(error)) {
                        return null; // Don't send to Sentry
                    }
                    return event;
                },
                // Set sample rate to reduce noise in production
                sampleRate: config.deploymentEnv === 'prod' ? 0.1 : 1.0,
            });
            this.isReportingServiceInitialized = true;
        }
    }
}

const errorService = new ErrorService();
export default errorService;
